/**
 * Iter 67fd — Vendor mini widget-attribution panel.
 *
 * Encourages adoption with a friendly callout: "Your widget has brought in
 * X bookings worth €Y this month". Includes a WoW trend pill (green when
 * up, red when down, blue "first window" when there's no previous data).
 *
 * Renders nothing if the vendor hasn't set a slug yet — the slug editor lives
 * in the same panel right below, so the vendor sees the prompt naturally.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar } from "recharts";
import { Globe2, TrendingUp, TrendingDown, Sparkles, Wallet, Loader2 } from "lucide-react";

function _fmtCurrency(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `€${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ChangePill({ pct, label = "vs prior" }) {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[11px] font-semibold">
        <Sparkles className="h-3 w-3" /> First window
      </span>
    );
  }
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const tone = up
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  const sign = up ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${tone} border text-[11px] font-semibold`}>
      <Icon className="h-3 w-3" />
      {sign}{pct}% {label}
    </span>
  );
}

export default function VendorWidgetAttributionMini({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/vendor/widget-attribution?days=${days}`)
      .then((res) => { if (active) setData(res.data); })
      .catch((e) => {
        if (active) {
          // Swallow — this is a non-essential analytics tile; never block the
          // rest of the widget panel.
          console.warn("widget-attribution mini:", formatApiError(e));
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  if (loading || !data || !data.slug_set) return null;

  const c = data.current || {};
  const noBookingsYet = (c.count || 0) === 0 && (data.previous?.count || 0) === 0;
  const sparklineData = (data.daily || []).map((d) => ({ ...d, widget: d.widget || 0 }));

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/40" data-testid="vendor-widget-attribution-mini">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-2xl bg-white shadow ring-1 ring-indigo-200 flex items-center justify-center flex-shrink-0">
              <Globe2 className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              {noBookingsYet ? (
                <>
                  <div className="text-sm font-bold text-neutral-900" data-testid="widget-mini-headline">
                    Your widget is live — share the link to start tracking conversions.
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    Bookings made via your micro-site / embed will appear here.
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold text-neutral-900" data-testid="widget-mini-headline">
                    <span className="text-indigo-700">{c.count}</span>
                    {" "}booking{c.count === 1 ? "" : "s"} from your widget
                    <span className="font-normal text-neutral-600"> · last {data.days} days</span>
                  </div>
                  <div className="text-xs text-neutral-600 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Wallet className="h-3 w-3" /> {_fmtCurrency(c.revenue)} revenue
                    </span>
                    <span className="text-neutral-400">·</span>
                    <span>{data.widget_share_pct}% of your bookings</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right side — WoW pill + sparkline */}
          {!noBookingsYet && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex flex-col items-end gap-1" data-testid="widget-mini-trend">
                <ChangePill pct={data.count_change_pct} label="bookings" />
                <ChangePill pct={data.revenue_change_pct} label="revenue" />
              </div>
              <div className="h-12 w-32" data-testid="widget-mini-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sparklineData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                    <Bar dataKey="widget" fill="#6366f1" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
